import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/styles";
import { Card, CardActionArea, Grid, TextField, Stack, Typography } from "@mui/material";
import { usePaidServices } from "@/hooks";
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import Document from "./Document";
import CustomCardSending from "./CustomCardSending";
import {
  activityLogSelector,
  sendBankStatementsLink,
  updateCustomer,
  uploadDocument,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import {
  ALLOWED_DOCUMENT_TYPE,
  ERRORS_MESSAGE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SUCCESS_MESSAGE,
  VALID_DOCUMENT_TYPE_EXTENSION,
} from "../../constants";
import RadioButtonGroup from "../Utils/RadioButtonGroup";
import { RichTooltip } from "../CommonComponents/RichTooltip";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const Input = styled("input")({
  display: "none",
});

const BSReportMonthlyValues = ({ value }) => {
  return (
    <>
      {typeof value === "object" && (
        <ul style={{ margin: "6px 0" }}>
          {Object.entries(value).map(([month, amount]) => (
            <li key={month} style={{ marginLeft: "20px" }}>
              <Typography variant="body2">
                {month}: {amount}
              </Typography>
            </li>
          ))}
        </ul>
      )}
      {typeof value === "string" && <Typography variant="body2">{value}</Typography>}
    </>
  );
};

const CustomerFinancialStatement = React.memo(({ customer, applicationId, isDisableEdit }) => {
  const { canUsePaidServices } = usePaidServices();
  const dispatch = useDispatch();
  const { isCustomerLogin } = useSelector(customerSelector);
  const isDisable = isCustomerLogin ? false : isDisableEdit;
  const activityLog = useSelector(activityLogSelector);
  const { bankStatementsRequest, bankStatementsLoading } = useSelector(userDetailsSelector);
  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      customer: { allowedBankStatementDays, isIllionDocIdFieldEnabled },
    },
  } = accessComponents;
  const fileInputBankStatement = useRef();
  const [bankStatementDays, setBankStatementDays] = useState(String(allowedBankStatementDays?.[0] || 90));
  const [uploadBankStatementLoading, setUploadBankStatementLoading] = useState(false);
  const [uploadBankStatementError, setUploadBankStatementError] = useState("");
  const [illionDocIdInput, setIllionDocIdInput] = useState(customer?.illionDocId ?? "");

  useEffect(() => {
    setIllionDocIdInput(customer?.illionDocId ?? "");
  }, [customer?._id, customer?.illionDocId]);

  const isCustomerEmail = customer?.emails?.[0]?.address;
  const isCustomerPhoneNumber = customer?.phoneNumbers?.[0]?.number;

  const isBankStatementDisabled =
    !isCustomerPhoneNumber || !isCustomerEmail || !canUsePaidServices || !bankStatementDays || isDisable;

  const filterActivityLog = (criteria) => activityLog?.filter(criteria);
  const isCustomerMatch = (item) => item?.customer === customer._id;
  const isSendBankStatementsLink = (item) => item?.name === "SendBankStatementsLink" && isCustomerMatch(item);
  const isBankStatementInsert = (item) =>
    item?.activityType === "BankStatement" && item?.name === "Insert" && isCustomerMatch(item);

  const bankStatementLinkSentList = filterActivityLog(isSendBankStatementsLink);
  const bankStatementCompletedList = filterActivityLog(isBankStatementInsert);

  const isBankStatementLinkSent = activityLog?.reduce(
    (latest, item) => (isSendBankStatementsLink(item) ? item : latest),
    null,
  );

  const isResendLink = isBankStatementLinkSent && customer?.bankStatements && bankStatementLinkSentList?.length >= 2;

  const isResendBankStatementCompleted =
    isResendLink &&
    customer?.bankStatements &&
    bankStatementCompletedList?.length === bankStatementLinkSentList?.length;

  const isBankStatementCompleted =
    isBankStatementLinkSent && customer?.bankStatements && (isResendLink ? isResendBankStatementCompleted : true);

  const bankStatementLink =
    !isBankStatementCompleted && isBankStatementLinkSent ? isBankStatementLinkSent?.values?.url : "";

  const handle = {
    setStatementsDays: (day) => {
      setBankStatementDays(day);
    },
    saveIllionDocId: (value) => {
      const trimmed = (value ?? "").trim();
      const current = (customer?.illionDocId ?? "").trim();
      if (trimmed === current) {
        return;
      }

      dispatch(
        updateCustomer({
          data: {
            customerId: customer._id,
            data: {
              application: applicationId,
              illionDocId: trimmed,
            },
          },
        }),
      ).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
          setIllionDocIdInput(customer?.illionDocId ?? "");
        }
      });
    },
    handleSendBankStatementsLink: (customerId, applicationId, mobile, bankStatementDays) => {
      dispatch(
        sendBankStatementsLink({
          data: {
            customerId,
            applicationId,
            bankStatementDays,
            mobile,
          },
        }),
      ).then((res) => {
        if (!res?.payload?.response_error) {
          dispatch(
            openSnackbar({
              message: SUCCESS_MESSAGE.bankStatements,
              intent: "success",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
    },
    getBankStatementLink(input) {
      if (typeof input === "string") {
        return input;
      } else if (typeof input === "object" && input !== null) {
        return input?.shortUrl || "";
      }
      return "";
    },
    uploadBankStatementDocuments: (event) => {
      const files = event.target.files;
      const newSelectedFiles = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ALLOWED_DOCUMENT_TYPE.includes(file.type)) {
          if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
            newSelectedFiles.push(file);
          } else {
            setUploadBankStatementError(ERRORS_MESSAGE.documentSizeMsg);
          }
        } else {
          setUploadBankStatementError(ERRORS_MESSAGE.documentTypeMsg);
        }
      }
      if (newSelectedFiles?.length) {
        setUploadBankStatementLoading(true);
        newSelectedFiles.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("applicationId", applicationId);
        formData.append("customerId", customer._id);
        formData.append("name", "Bank statements");

        dispatch(uploadDocument({ formData }))
          .then((res) => {
            if (res?.payload?.data) {
              dispatch(
                openSnackbar({
                  message: SUCCESS_MESSAGE.fileUploadSuccess,
                  intent: "success",
                }),
              );
            } else {
              dispatch(
                openSnackbar({
                  message: ERRORS_MESSAGE.fetchErrorMsg,
                  intent: "danger",
                }),
              );
            }
          })
          .finally(() => {
            setUploadBankStatementLoading(false);
          });
        event.target.value = "";
        setUploadBankStatementError("");
      }
    },
  };

  return (
    <React.Fragment>
      <Typography fontWeight={600} style={{ marginBottom: "15px" }}>
        Bank Statements
      </Typography>

      {customer?.bankStatements && (
        <>
          <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: "5px" }}>
            Bank accounts summary (current balance)
          </Typography>
          <Grid item container style={{ margin: "0 0 20px 0" }} spacing={2}>
            {customer?.bankStatements &&
              customer?.bankStatements?.bankAccounts?.map((bank, i) => (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{bank.bankName}</Typography>
                  {bank.bankAccounts.map((account) => (
                    <Typography variant="body2">
                      <strong>{account.accountName}</strong>: {account.currentBalance}
                    </Typography>
                  ))}
                </Grid>
              ))}
          </Grid>
        </>
      )}

      {customer?.bankStatements && (
        <React.Fragment>
          <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: "5px" }}>
            Credits
          </Typography>
          <Grid item container style={{ margin: "0 0 20px 0" }}>
            {customer?.bankStatements &&
              customer?.bankStatements?.decisionMetrics?.map((metric, i) => {
                if (i < 3)
                  return (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{metric.name}</Typography>
                      <Typography variant="body2">{metric.value}</Typography>
                    </Grid>
                  );
                return null;
              })}
          </Grid>
        </React.Fragment>
      )}

      {customer?.bankStatements && (
        <React.Fragment>
          <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: "5px" }}>
            Responsible lending flags
          </Typography>
          <Grid item container style={{ margin: "0 0 20px 0" }}>
            {customer?.bankStatements &&
              customer?.bankStatements?.decisionMetrics?.map((metric, i) => {
                if (i > 2 && i < 7)
                  return (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{metric.name}</Typography>
                      <Typography variant="body2">{metric.value}</Typography>
                    </Grid>
                  );
                return null;
              })}
          </Grid>
        </React.Fragment>
      )}

      {customer?.bankStatements && (
        <React.Fragment>
          <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: "5px" }}>
            Liabilities
          </Typography>
          <Grid item container style={{ margin: "0 0 20px 0" }}>
            {customer?.bankStatements &&
              customer?.bankStatements?.decisionMetrics?.map((metric, i) => {
                if (i > 6 && i < 10)
                  return (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{metric.name}</Typography>
                      <Typography variant="body2">{metric.value}</Typography>
                    </Grid>
                  );
                return null;
              })}
          </Grid>
        </React.Fragment>
      )}

      {customer?.bankStatements && (
        <React.Fragment>
          <Typography variant="subtitle2" fontWeight={600} style={{ marginBottom: "5px" }}>
            Debits
          </Typography>
          <Grid item container style={{ margin: "0 0 20px 0" }}>
            {customer?.bankStatements &&
              customer?.bankStatements?.decisionMetrics?.map((metric, i) => {
                if (i > 9)
                  return (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{metric.name}</Typography>
                      <BSReportMonthlyValues value={metric.value} />
                    </Grid>
                  );
                return null;
              })}
          </Grid>
        </React.Fragment>
      )}

      <Grid item xs={12} sx={{ minWidth: 0, marginBottom: "20px" }}>
        <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
          {customer?.documents?.length > 0 &&
            customer?.documents
              ?.filter((doc) => ["Bank statements"].includes(doc.name))
              .map((doc, idx) => (
                <Document key={doc?._id ?? idx} document={doc} customer={customer} name="financialStatement" />
              ))}
        </Stack>
      </Grid>

      <Grid container spacing={2} sx={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2">Bank statements days</Typography>
          <RadioButtonGroup
            required
            disabled={isDisable}
            name={"statementsDays"}
            value={bankStatementDays}
            handleValue={(value) => {
              handle.setStatementsDays(value);
            }}
            optionStyle={{
              width: "50%",
              padding: "8px",
              minHeight: "20px",
            }}
            options={allowedBankStatementDays?.map((day) => ({ icon: String(day), label: String(day) }))}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {isIllionDocIdFieldEnabled && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Illion document ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                disabled={isDisable}
                value={illionDocIdInput}
                onChange={(event) => setIllionDocIdInput(event.target.value)}
                onBlur={() => handle.saveIllionDocId(illionDocIdInput)}
                placeholder="Illion document ID"
                inputProps={{ maxLength: 20 }}
              />
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <RichTooltip title={canUsePaidServices ? null : <PaidServicesTooltip />}>
            <Card>
              <CardActionArea
                disableRipple={isBankStatementDisabled ? true : false}
                onClick={() => {
                  if (isBankStatementDisabled) return;
                  handle.handleSendBankStatementsLink(
                    customer._id,
                    applicationId,
                    customer?.phoneNumbers.slice(-1)[0],
                    bankStatementDays,
                  );
                }}
              >
                <CustomCardSending
                  title={"Get bank statements"}
                  disabled={isBankStatementDisabled}
                  description={`Send a self service link and get
              access to their bank statements via illion Bank Statements.`}
                  imagePath={"/static/illion-logo.png"}
                  loadingState={bankStatementsLoading}
                  financialState={true}
                  icon={false}
                  lastTitle={"Sending SMS link..."}
                />
              </CardActionArea>
            </Card>
            {(!isCustomerEmail || !isCustomerPhoneNumber) && (
              <Typography variant="caption">
                Sending a bank statements link requires a customer {!isCustomerEmail && "email, "}{" "}
                {!isCustomerPhoneNumber && "mobile number"}
              </Typography>
            )}
            {bankStatementsRequest && <Typography variant="caption">{SUCCESS_MESSAGE.bankStatements}</Typography>}
          </RichTooltip>
        </Grid>
        {accessComponents.application.customer.uploadBankStatementsEnabled && (
          <Grid item xs={12} sm={6}>
            <Card>
              <Input
                accept={VALID_DOCUMENT_TYPE_EXTENSION}
                ref={fileInputBankStatement}
                id={`upload-bank-statements-${customer._id}`}
                multiple
                type="file"
                value={null}
                onChange={(event) => handle.uploadBankStatementDocuments(event)}
              />
              <CardActionArea onClick={() => fileInputBankStatement.current.click()}>
                <CustomCardSending
                  title={"Upload bank statement documents"}
                  description={`Upload bank statement documents from files stored locally on your
                computer.`}
                  imagePath={""}
                  loadingState={uploadBankStatementLoading}
                  financialState={true}
                  icon={false}
                  lastTitle={"Upload bank statement document..."}
                  disabled={isDisable}
                />
              </CardActionArea>
            </Card>
            {uploadBankStatementError && (
              <Typography variant="body2" style={{ color: "red" }}>
                {uploadBankStatementError}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>

      {bankStatementLink && handle.getBankStatementLink(bankStatementLink) && (
        <Stack
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
            marginTop: "15px",
          }}
        >
          <Typography fontWeight={600}>Link :</Typography>

          <Typography
            component="a"
            href={handle.getBankStatementLink(bankStatementLink)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "underline",
              color: "primary.main",
              cursor: "pointer",
              "&:hover": {
                color: "secondary.main",
              },
            }}
          >
            {handle.getBankStatementLink(bankStatementLink)}
          </Typography>
        </Stack>
      )}
    </React.Fragment>
  );
});

export default CustomerFinancialStatement;
